import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  Check,
  ChevronDown,
  Download,
  Paperclip,
  Pencil,
  X,
  Loader,
  Scale,
} from "lucide-react";
import ConsumerHeader from "./ConsumerHeader";
import Footer from "./Footer";
import SEO from "./SEO";
import ConsumerResponseCard from "./ConsumerResponseCard";
import ConsumerSummary from "./ConsumerSummary";
import SecondQuestionReminder from "./SecondQuestionReminder";
import ModelIcon from "./dashboard/ModelIcon";
import EditorModal from "./dashboard/EditorModal";
import FeaturedAttorneys from "./FeaturedAttorneys";
import StatewideBanner from "./StatewideBanner";
import { useTheme } from "../context/ThemeContext";
import { useEditor } from "../hooks/useEditor";
import { convertTextToHtml } from "../utils/textProcessing";
import { handleDownloadPDF } from "../utils/exportUtils";
import { buildSummaryExportHtml } from "../utils/consumerSummaryExport";
import { US_STATES, STATE_STORAGE_KEY } from "../constants/directoryOptions";
import "./LegalQuestion.css";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

/** Kept in sync with CONSUMER_MODELS in backend/routes/consumer.js. */
const CONSUMER_MODELS = [
  "ChatGPT",
  "Claude",
  "Gemini",
  "Grok",
  "Perplexity",
];

/**
 * Mirrors MAX_MODELS_PER_REQUEST in backend/routes/consumer.js. Deliberately
 * one below the number of models offered: five answers to read is past the
 * point of being useful, and every extra model is a paid completion on an
 * unauthenticated page. Once four are ticked the rest disable themselves.
 */
const MAX_MODELS = 4;


export default function LegalQuestion() {
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  const [question, setQuestion] = useState("");
  const [usState, setUsState] = useState("");
  const [stateAutoDetected, setStateAutoDetected] = useState(false);
  // Set the moment the visitor uses the picker themselves. A ref, not state,
  // because the in-flight IP lookup reads it from a stale closure.
  const stateTouchedRef = useRef(false);
  const [models, setModels] = useState(["ChatGPT", "Claude"]);
  const [agreed, setAgreed] = useState(false);

  const [file, setFile] = useState(null);
  const [documentText, setDocumentText] = useState("");
  const [extracting, setExtracting] = useState(false);

  const [analyzing, setAnalyzing] = useState(false);
  const [responses, setResponses] = useState([]);
  const [analysisDone, setAnalysisDone] = useState(false);
  // Featured attorneys matched to the question. Set to a loading marker the
  // moment an analysis starts and filled in by the stream's `featured` event,
  // which the backend sends as soon as its classifier + directory lookup are
  // done — usually well before the first model finishes.
  const [featured, setFeatured] = useState(null);
  // Server-side id of this question's record, from the stream's `done` event.
  // Sent with the summary request so the summary is stored alongside the
  // question. Never displayed.
  const [questionId, setQuestionId] = useState(null);

  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState("");
  // Set only when the visitor saves an edit — their markup then wins over the
  // parsed blocks, both on screen and in the download.
  const [summaryFormatted, setSummaryFormatted] = useState("");
  // Open by default: the summary is the reason the visitor is on the page.
  const [summaryOpen, setSummaryOpen] = useState(true);

  const [error, setError] = useState("");
  // The 403 body carries when the allowance comes back; the message alone
  // leaves the visitor with nothing to act on.
  const [limitNotice, setLimitNotice] = useState(null);

  // The reminder is shown once, on the last free question of the window —
  // never on the first, and never twice for the same submission. Product
  // decision (QA 12a): the pop-up is the confirmation step; after "Go Back
  // and Review" the next Analyze click submits without asking again.
  const [reminderOpen, setReminderOpen] = useState(false);
  const [questionLimit, setQuestionLimit] = useState(2);
  const reminderShownRef = useRef(false);

  // Stays null unless a real count comes back, so the hero pill is simply
  // absent rather than showing an invented figure.
  const [questionsAnalyzed, setQuestionsAnalyzed] = useState(null);

  // The same rich text editor the attorney workspace uses, so an edited answer
  // exports through exactly one code path.
  const {
    isOpen: editorOpen,
    title: editorTitle,
    content: editorContent,
    meta: editorMeta,
    openEditor,
    closeEditor,
  } = useEditor();

  const fileInputRef = useRef(null);
  const responsesRef = useRef(null);
  const summaryRef = useRef(null);

  // Streaming deltas arrive far faster than the screen refreshes — a single
  // model emits ~800 of them. Buffer them and apply one state update per
  // animation frame, otherwise every token re-renders (and re-highlights) the
  // whole card and the page stutters badly on mobile.
  const pendingDeltasRef = useRef({});
  const pendingSummaryRef = useRef("");
  const flushFrameRef = useRef(null);

  // State comes from, in order of trust: what the visitor picked last time,
  // then a guess from their IP. The guess only fills an empty picker — it is
  // never allowed to overwrite a real choice, including one made while the
  // lookup was still in flight. If both miss, the field stays empty and the
  // submit check makes them choose.
  useEffect(() => {
    let saved = null;
    try {
      const stored = localStorage.getItem(STATE_STORAGE_KEY);
      if (stored && US_STATES.includes(stored)) saved = stored;
    } catch (e) {
      // Private browsing — nothing to restore.
    }

    if (saved) {
      setUsState(saved);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/geo/state`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled || stateTouchedRef.current) return;
        if (data?.state && US_STATES.includes(data.state)) {
          setUsState(data.state);
          setStateAutoDetected(true);
          // Deliberately not written to localStorage: a guess should not come
          // back on other pages looking like something the visitor chose.
        }
      } catch (e) {
        // No detection — the visitor picks a state by hand.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/stats/legal-questions-analyzed`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Number.isFinite(data?.count)) {
          setQuestionsAnalyzed(data.count);
        }
      } catch (e) {
        // No count available — the pill stays hidden.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onStateChange = (value) => {
    // Marks the picker as the visitor's own, so a late-arriving IP guess backs
    // off and the "filled in from your location" note goes away.
    stateTouchedRef.current = true;
    setStateAutoDetected(false);
    setUsState(value);
    try {
      localStorage.setItem(STATE_STORAGE_KEY, value);
    } catch (e) {
      // no-op
    }
  };

  const toggleModel = (model) => {
    setModels((prev) => {
      if (prev.includes(model)) return prev.filter((m) => m !== model);
      if (prev.length >= MAX_MODELS) return prev;
      return [...prev, model];
    });
  };

  const succeeded = useMemo(
    () => responses.filter((r) => r.text && !r.error),
    [responses],
  );

  // The summary step only opens once the analysis has come back and at least
  // one model actually produced a response.
  const canSummarize = analysisDone && succeeded.length > 0;

  const scrollTo = (ref) => {
    setTimeout(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 60);
  };

  // ── Document upload ───────────────────────────────────────────────────────
  const onFilePick = async (e) => {
    const picked = e.target.files && e.target.files[0];
    if (!picked) return;

    setError("");
    setExtracting(true);
    try {
      const form = new FormData();
      form.append("file", picked);
      const res = await fetch(`${API_URL}/consumer/extract`, {
        method: "POST",
        credentials: "include",
        body: form,
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || "We could not read that document.");
      }
      setFile(picked);
      setDocumentText(data.text);
      if (data.truncated) {
        setError(
          "Your document was long, so only the first part will be included in the analysis.",
        );
      }
    } catch (err) {
      setFile(null);
      setDocumentText("");
      setError(err.message);
    } finally {
      setExtracting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeFile = () => {
    setFile(null);
    setDocumentText("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Apply everything buffered since the last frame in one state update.
  const flushDeltas = () => {
    if (flushFrameRef.current !== null) {
      cancelAnimationFrame(flushFrameRef.current);
      flushFrameRef.current = null;
    }

    const pending = pendingDeltasRef.current;
    pendingDeltasRef.current = {};
    if (Object.keys(pending).length) {
      setResponses((prev) =>
        prev.map((r) =>
          pending[r.model] ? { ...r, text: r.text + pending[r.model] } : r,
        ),
      );
    }

    const summaryChunk = pendingSummaryRef.current;
    pendingSummaryRef.current = "";
    if (summaryChunk) {
      setSummary((prev) => prev + summaryChunk);
    }
  };

  const scheduleFlush = () => {
    if (flushFrameRef.current !== null) return;
    flushFrameRef.current = requestAnimationFrame(() => {
      flushFrameRef.current = null;
      flushDeltas();
    });
  };

  const pushDelta = (model, delta) => {
    if (!delta) return;
    pendingDeltasRef.current[model] =
      (pendingDeltasRef.current[model] || "") + delta;
    scheduleFlush();
  };

  const pushSummaryDelta = (delta) => {
    if (!delta) return;
    pendingSummaryRef.current += delta;
    scheduleFlush();
  };

  // Drop any queued frame if the visitor navigates away mid-stream.
  useEffect(() => {
    return () => {
      if (flushFrameRef.current !== null) {
        cancelAnimationFrame(flushFrameRef.current);
      }
    };
  }, []);

  // Read one SSE response, invoking `onEvent(name, data)` per event. Shared by
  // the analyze and summary handlers so both parse the wire format identically.
  const readEventStream = async (response, onEvent) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let currentEvent = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      // The trailing element is whatever arrived mid-line; keep it buffered.
      buffer = lines.pop() || "";

      for (const line of lines) {
        if (line.startsWith("event: ")) {
          currentEvent = line.slice(7).trim();
        } else if (line.startsWith("data: ")) {
          try {
            onEvent(currentEvent, JSON.parse(line.slice(6)));
          } catch (e) {
            console.error("Malformed SSE payload:", e, line);
          }
          currentEvent = null;
        } else if (line.trim() === "") {
          currentEvent = null;
        }
      }
    }
  };

  // A stream that fails before its first byte is still a normal JSON response
  // with a real status code. Returns the parsed body when that has happened.
  const nonStreamBody = async (response) => {
    const type = response.headers.get("content-type") || "";
    if (response.ok && type.includes("text/event-stream")) return null;
    return response.json().catch(() => ({}));
  };

  /**
   * How many of this visitor's free questions are already spent.
   *
   * Answers -1 when the count cannot be read, which skips the reminder rather
   * than guessing: a wrong warning is worse than none, and the backend is the
   * thing that actually enforces the allowance either way.
   */
  const readQuestionsUsed = async () => {
    try {
      const res = await fetch(`${API_URL}/ai/anon-usage`, {
        credentials: "include",
      });
      if (!res.ok) return { used: -1, limit: 0 };
      const data = await res.json();
      // Admins are uncapped; every other signed-in user gets the same rolling
      // window as a guest and the same numbers back.
      if (data?.authenticated && data?.unlimited) return { used: -1, limit: 0 };
      return {
        used: Number.isFinite(data?.dailyUsed) ? data.dailyUsed : -1,
        limit: Number.isFinite(data?.dailyLimit) ? data.dailyLimit : 0,
      };
    } catch (e) {
      return { used: -1, limit: 0 };
    }
  };

  /**
   * The submit button. Everything the visitor has to fix is checked here, so
   * the reminder is never raised over a question that would be rejected
   * anyway, and the reminder itself is raised only for the last free question.
   */
  /**
   * Everything the visitor has to fix before anything is sent, in the order
   * the steps appear on screen so the message points at the first incomplete
   * one. Shared by both entry points — when they each had their own copy the
   * two drifted apart.
   *
   * @returns {string} the first problem, or "" when the form is ready.
   */
  const firstProblem = () => {
    if (!question.trim()) return "Please describe your legal question.";
    if (!models.length) return "Please select at least one AI model.";
    if (!usState) return "Please select your state.";
    if (!agreed) {
      return "Please agree to the Terms of Service and Privacy Policy to continue.";
    }
    return "";
  };

  const handleAnalyzeClick = async () => {
    setError("");
    setLimitNotice(null);

    const problem = firstProblem();
    if (problem) {
      setError(problem);
      return;
    }

    if (!reminderShownRef.current) {
      const { used, limit } = await readQuestionsUsed();
      // The second question of the window, and only that one. Keyed to the
      // count rather than to "the last one before the limit" so it stays the
      // second question if the allowance ever changes.
      if (used === 1) {
        reminderShownRef.current = true;
        setQuestionLimit(limit);
        setReminderOpen(true);
        return;
      }
    }

    handleAnalyze();
  };

  // ── Step 1: Analyze. Deliberately separate from the summary handler. ──────
  const handleAnalyze = async () => {
    setError("");
    setLimitNotice(null);

    // Also checked here, not only in the click handler: the reminder's submit
    // button calls straight into this.
    const problem = firstProblem();
    if (problem) {
      setError(problem);
      return;
    }

    setAnalyzing(true);
    setResponses([]);
    setAnalysisDone(false);
    setFeatured({ loading: true, listings: [] });
    setQuestionId(null);
    setSummary("");
    setSummaryFormatted("");

    try {
      const res = await fetch(`${API_URL}/consumer/analyze-stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          question,
          state: usState,
          models,
          documentText,
          documentName: file ? file.name : "",
        }),
      });

      const failed = await nonStreamBody(res);
      if (failed) {
        // 429 is what the API sends for a used-up daily allowance (guest or
        // signed-in); 403 is kept for older deployments.
        if ((res.status === 429 || res.status === 403) && failed.anonLimitExceeded) {
          setLimitNotice({
            message:
              failed.message ||
              "You've already used your free legal analysis. You can submit another legal question 24 hours after your last completed analysis.",
            nextAvailableAt: failed.nextAvailableAt || null,
          });
          return;
        }
        throw new Error(failed.error || "We could not analyze your question.");
      }

      // Cards appear immediately and fill in as tokens arrive, so scroll now
      // rather than waiting for the slowest model to finish.
      setResponses(
        models.map((m) => ({ model: m, text: "", error: null, loading: true })),
      );
      scrollTo(responsesRef);

      await readEventStream(res, (event, data) => {
        if (event === "start") {
          setResponses(
            (data.models || models).map((m) => ({
              model: m,
              text: "",
              error: null,
              loading: true,
            })),
          );
        } else if (event === "modelDelta") {
          pushDelta(data.model, data.delta || "");
        } else if (event === "modelDone") {
          flushDeltas();
          setResponses((prev) =>
            prev.map((r) =>
              r.model === data.model
                ? { ...r, text: data.text, loading: false }
                : r,
            ),
          );
        } else if (event === "modelError") {
          flushDeltas();
          setResponses((prev) =>
            prev.map((r) =>
              r.model === data.model
                ? { ...r, error: data.error, loading: false }
                : r,
            ),
          );
        } else if (event === "featured") {
          setFeatured({
            loading: false,
            listings: data.listings || [],
            matchType: data.matchType || "none",
            category: data.category || "Other",
          });
        } else if (event === "done") {
          flushDeltas();
          setResponses(
            (data.responses || []).map((r) => ({
              ...r,
              text: r.text || "",
              loading: false,
            })),
          );
          // The backend always sends `featured` before `done`; this only
          // matters if that event was lost, so the section cannot sit on its
          // skeletons forever.
          setFeatured((prev) =>
            prev && prev.loading ? { ...prev, loading: false } : prev,
          );
          setQuestionId(data.questionId || null);
          setAnalysisDone(true);
        }
      });
    } catch (err) {
      setError(err.message);
    } finally {
      flushDeltas();
      setAnalyzing(false);
    }
  };

  // ── Step 2: Summary. A separate handler and a separate endpoint. ──────────
  const handleGenerateSummary = async () => {
    if (!canSummarize || summarizing) return;
    setError("");
    setSummarizing(true);

    setSummary("");
    setSummaryFormatted("");

    try {
      const res = await fetch(`${API_URL}/consumer/summary-stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        // The summary is generated from the responses the server stored for
        // this question; only the reference travels.
        body: JSON.stringify({ questionId }),
      });

      const failed = await nonStreamBody(res);
      if (failed) {
        throw new Error(failed.error || "We could not generate your summary.");
      }

      scrollTo(summaryRef);

      let streamError = null;
      await readEventStream(res, (event, data) => {
        if (event === "delta") {
          pushSummaryDelta(data.delta || "");
        } else if (event === "done") {
          flushDeltas();
          setSummary(data.summary);
        } else if (event === "error") {
          streamError = data.error || "We could not generate your summary.";
        }
      });

      if (streamError) throw new Error(streamError);
    } catch (err) {
      setError(err.message);
    } finally {
      flushDeltas();
      setSummarizing(false);
    }
  };

  // An edited answer carries its own HTML; everything else is converted from
  // the raw model text on the way out.
  const responseHtml = (r) =>
    r.formatted || (r.text ? convertTextToHtml(r.text) : "");

  const exportResponse = (r) => {
    handleDownloadPDF(responseHtml(r), `${r.model} — Legal Analysis`);
  };

  const editSummary = () => {
    openEditor(
      "summary",
      "Your LawFusion Summary",
      summaryFormatted || convertTextToHtml(summary),
    );
  };

  // EditorModal hands back the edited HTML plus its plain text. The summary is
  // the only editable thing on this page — the model answers are the model's,
  // not the visitor's — so there is one case to handle.
  const handleEditorSave = (content, plainText) => {
    if ((editorMeta || {}).type !== "summary") return;
    setSummary((prev) => plainText || prev);
    setSummaryFormatted(content);
  };

  // An absolute local time rather than a countdown — this page can sit open
  // for hours, and "in 3 hours" would quietly become wrong.
  const limitResetText = useMemo(() => {
    const iso = limitNotice?.nextAvailableAt;
    if (!iso) return "";
    const when = new Date(iso);
    if (Number.isNaN(when.getTime())) return "";
    return `You can ask another question after ${when.toLocaleString(undefined, {
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "numeric",
      minute: "2-digit",
    })}.`;
  }, [limitNotice]);

  const dark = darkMode;

  // "Based on ChatGPT and Claude" reads better than a bare comma list.
  const modelsText = useMemo(() => {
    const names = succeeded.map((r) => r.model);
    if (!names.length) return "no responses";
    if (names.length === 1) return names[0];
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  }, [succeeded]);

  // Exports the summary as the coloured blocks the visitor just read, rather
  // than as flat text — see consumerSummaryExport.
  const downloadSummary = () => {
    handleDownloadPDF(
      buildSummaryExportHtml({
        summary,
        formatted: summaryFormatted,
        modelsText,
      }),
      "Your LawFusion Summary",
      { styled: true },
    );
  };

  return (
    <>
      <SEO
        title="Ask a Legal Question"
        description="Describe your legal question and compare answers from multiple AI models. General legal information, not legal advice."
        path="/ask"
      />
      <ConsumerHeader />

      <main className={`lq-main ${dark ? "lq-dark" : ""}`}>
        <div className="lq-wrap">
          {/* ── Hero ─────────────────────────────────────────────────────── */}
          <section className="lq-hero">
            <h1>Compare Legal AI Answers. Get Clearer Next Steps.</h1>
            <p>
              Compare multiple AI answers, see where they agree or differ, get a{" "}
              <strong>one-click summary</strong>, and find an attorney if you
              need one.
            </p>
            <img
              className="lq-hero-logos"
              src="/ai_logos_ask_page_tight.jpg"
              width="946"
              height="316"
              alt="ChatGPT, Claude, Gemini, Grok, Perplexity"
            />
            {/* Only rendered once a real count is available — never a placeholder. */}
            {questionsAnalyzed !== null && (
              <div className="lq-stat-pill">
                <Check size={14} className="lq-stat-tick" aria-hidden="true" />
                <span>
                  <strong>{questionsAnalyzed.toLocaleString()}</strong> Legal
                  Questions Analyzed
                </span>
              </div>
            )}
          </section>

          {/* ── Question form ────────────────────────────────────────────── */}
          <section className="lq-card">
            {/* Step 1 — question + optional document */}
            <div className="lq-step">
              <h2 className="lq-step-title" id="lq-step-1">
                Step 1 — Describe your legal question or upload a document
              </h2>
              <p className="lq-step-help">
                <strong>The more detail you provide, the better the analysis.</strong>{" "}
                Include important dates, amounts, documents, and what happened.
              </p>
              <textarea
                id="lq-question"
                aria-labelledby="lq-step-1"
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="My landlord kept my $2,000 security deposit after I moved out and says I caused damage. I believe it was normal wear and tear. I moved out three weeks ago and have not received an itemized list of deductions."
                className="lq-textarea"
              />

              <div className="lq-upload-row">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx,.txt,.md"
                  onChange={onFilePick}
                  style={{ display: "none" }}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  disabled={extracting}
                  className="lq-upload-btn"
                >
                  {extracting ? (
                    <>
                      <Loader size={15} className="lq-spin" aria-hidden="true" />
                      Reading document…
                    </>
                  ) : (
                    <>
                      <Paperclip size={15} aria-hidden="true" />
                      Upload a document
                    </>
                  )}
                </button>
                {/* "Optional" is a badge rather than the first word of the
                    hint: set in the same grey as the format list it was easy
                    to read straight past, and someone who misses it thinks a
                    document is required to ask a question. */}
                <span className="lq-upload-optional">Optional</span>
                {/* Formats are the ones backend/routes/consumer.js can actually
                    extract — advertising more would fail at upload. */}
                <span className="lq-upload-hint">
                  PDF, DOCX, TXT, MD · up to 10 pages / 4,000 words
                </span>
              </div>

              {file && (
                <div className="lq-file">
                  <Paperclip size={14} aria-hidden="true" />
                  <span className="lq-file-name">{file.name}</span>
                  <button
                    type="button"
                    onClick={removeFile}
                    aria-label="Remove attachment"
                    title="Remove attachment"
                    className="lq-file-remove"
                  >
                    <X size={13} />
                  </button>
                </div>
              )}
            </div>

            {/* Step 2 — models. Ahead of the state picker on purpose:
                comparing several AIs is the thing that makes LawFusion worth
                using, so it should be the next thing a visitor sees. The state
                is usually filled in for them, so it costs nothing to move it
                down. */}
            <div className="lq-step">
              <h2 className="lq-step-title">
                Step 2 — Select up to {MAX_MODELS} AI models
              </h2>
              <p className="lq-step-help">
                Compare multiple AI perspectives on the same legal question.
              </p>

              <div className="lq-models">
                {CONSUMER_MODELS.map((m) => {
                  const on = models.includes(m);
                  const atLimit = !on && models.length >= MAX_MODELS;
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => toggleModel(m)}
                      disabled={atLimit}
                      aria-pressed={on}
                      className={`lq-model-btn ${on ? "is-on" : ""}`}
                    >
                      <span className="lq-model-identity">
                        <span className="lq-model-badge" aria-hidden="true">
                          <ModelIcon model={m} size={15} colored />
                        </span>
                        {m}
                      </span>
                      <span className="lq-model-check" aria-hidden="true">
                        {on && <Check size={12} strokeWidth={3.5} />}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Step 3 — state, consent, submit */}
            <div className="lq-step lq-step-last">
              <h2 className="lq-step-title">
                <label htmlFor="lq-state">
                  Step 3 — Select your state{" "}
                  <span className="lq-req" aria-hidden="true">
                    *
                  </span>
                </label>
              </h2>
              <select
                id="lq-state"
                value={usState}
                onChange={(e) => onStateChange(e.target.value)}
                className="lq-select"
                required
              >
                <option value="">Select a state…</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <p className="lq-step-note">
                {stateAutoDetected
                  ? "Filled in from your location — change it if that is not where your issue is."
                  : "Legal rules can vary by state."}
              </p>

              {/* Consent — gates the Analyze button. It carries its own
                  Privacy Policy link, so a separate privacy line above it just
                  said the same thing twice. */}
              <label className="lq-consent">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                />
                <span>
                  {/* New tab: following these in place threw away a
                      half-written question and the answers already on screen. */}
                  I agree to the{" "}
                  <Link
                    to="/terms-of-service"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link
                    to="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>.
                </span>
              </label>

              <button
                type="button"
                onClick={handleAnalyzeClick}
                disabled={!agreed || analyzing}
                className="lq-primary"
              >
                {analyzing ? "Analyzing your question…" : "Analyze My Legal Question"}
              </button>

              <p className="lq-disclaimer">
                General information only. Not legal advice, and not a substitute
                for an attorney.
              </p>

              {error && <div className="lq-error">{error}</div>}
              {limitNotice && (
                <div className="lq-limit" role="alert">
                  <AlertTriangle size={17} aria-hidden="true" />
                  <div>
                    <p>{limitNotice.message}</p>
                    {limitResetText && (
                      <p className="lq-limit-reset">{limitResetText}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ── Individual AI responses ──────────────────────────────────── */}
          {responses.length > 0 && (
            <div ref={responsesRef} className="lq-responses">
              {responses.map((r) => (
                <ConsumerResponseCard
                  key={r.model}
                  model={r.model}
                  html={responseHtml(r)}
                  loading={!!r.loading}
                  error={r.error || ""}
                  onExport={() => exportResponse(r)}
                />
              ))}
            </div>
          )}

          {/* ── LawFusion Summary ────────────────────────────────────────── */}
          {analysisDone && (
            <section
              ref={summaryRef}
              className="lq-card"
              data-collapsed={summary && !summaryOpen ? "true" : undefined}
            >
              <div className="lq-summary-head">
                <div>
                  <h2 className="lq-summary-title">Your LawFusion Summary</h2>
                  <div className="lq-summary-sub">
                    Based on <strong>{modelsText}</strong>
                  </div>
                </div>
                <div className="lq-summary-actions">
                  {/* Only worth offering once there is a summary to edit. */}
                  {summary && !summarizing && (
                    <button
                      type="button"
                      onClick={editSummary}
                      className="lq-ghost-btn"
                    >
                      <Pencil size={14} aria-hidden="true" />
                      Edit
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={summary ? downloadSummary : handleGenerateSummary}
                    disabled={!canSummarize || summarizing}
                    className={`lq-summary-btn ${summary ? "is-ready" : ""}`}
                  >
                    {summarizing ? (
                      "Generating…"
                    ) : summary ? (
                      <>
                        <Download size={16} aria-hidden="true" /> Download Summary
                      </>
                    ) : (
                      "Generate My Summary"
                    )}
                  </button>
                  {/* Nothing to fold away until a summary exists. */}
                  {summary && (
                    <button
                      type="button"
                      className="lq-toggle"
                      onClick={() => setSummaryOpen((v) => !v)}
                      aria-expanded={summaryOpen}
                      aria-controls="lq-summary-panel"
                      aria-label={
                        summaryOpen ? "Collapse summary" : "Expand summary"
                      }
                      title={summaryOpen ? "Collapse" : "Expand"}
                    >
                      <ChevronDown size={17} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>

              <div className="lq-collapse" id="lq-summary-panel">
                <div className="lq-collapse-inner">
                  <ConsumerSummary
                    summary={summary}
                    formatted={summaryFormatted}
                  />
                </div>
              </div>
            </section>
          )}

          {/* ── Featured attorneys ─────────────────────────────────────────
              Shown from the moment an analysis starts, not only once it is
              done: the match runs in parallel with the models on the backend
              and this section fills in on its own the moment it lands. */}
          {(analyzing || analysisDone) && (
            <FeaturedAttorneys
              listings={featured?.listings || []}
              loading={!!featured?.loading}
              matchType={featured?.matchType || "none"}
              category={featured?.category || "Other"}
              usState={usState}
            />
          )}

          {/* ── Find an attorney ─────────────────────────────────────────── */}
          {analysisDone && (
            <section className="lq-cta">
              <div className="lq-cta-left">
                <div className="lq-cta-icon">
                  <Scale size={24} />
                </div>
                <div>
                  <h2>Want an attorney to review your situation?</h2>
                  <p>Find attorneys who handle this type of legal issue.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => navigate("/find-attorney")}
                className="lq-cta-btn"
              >
                Find an Attorney
              </button>
            </section>
          )}

          {/* Featured placement, shown before a question is asked so a visitor
              can see the slot exists at all. Once an analysis starts, the
              fuller FeaturedAttorneys card above takes over — running both
              would be the same message twice. */}
          {!analyzing && !analysisDone && (
            <section className="lq-featured-slot">
              <div className="lq-featured-slot-icon" aria-hidden="true">
                <Scale size={22} />
              </div>
              <div className="lq-featured-slot-body">
                <p className="lq-featured-slot-eyebrow">
                  Featured Attorneys/Paralegals
                </p>
                <h2>Relevant Featured Attorneys/Paralegals May Appear Here</h2>
              </div>
            </section>
          )}

          <section className="lq-note">
            LawFusion provides general legal information, not legal advice. The
            information here is not a substitute for an attorney. Every situation
            is different.
          </section>

          {/* Must stay LAST on the page — see StatewideBanner for the rules. */}
          <StatewideBanner usState={usState} />
        </div>
      </main>

      <Footer audience="consumer" />

      {/* Portalled to <body>, so it sits outside the page chrome. */}
      {reminderOpen && (
        <SecondQuestionReminder
          limit={questionLimit}
          onReview={() => setReminderOpen(false)}
          onSubmit={() => {
            setReminderOpen(false);
            handleAnalyze();
          }}
        />
      )}

      <EditorModal
        isOpen={editorOpen}
        title={editorTitle}
        content={editorContent}
        onClose={closeEditor}
        onSave={handleEditorSave}
        darkMode={dark}
      />
    </>
  );
}
